import i18n from '../i18n'

/**
 * The review screen's rules (design page 7, `review`; P-core-3 of the
 * go-live plan), mirrored from the Android app's `ReviewScreen.kt`. The
 * sentence about the take's length against the reference went with the
 * no-explanatory-text rule (owner, 2026-10-14/16).
 */

const t = (key, options) => i18n.t(key, { ns: 'review', ...options })

/** The design's strips: 52 bars, 52px tall; a bar is at least a 4px stub. */
export const COMPARE_BARS = 52
export const COMPARE_BAR_MAX_PX = 52
export const COMPARE_BAR_MIN_PX = 4

/**
 * The take's own shape: the RMS of each of `bars` slices of the samples,
 * normalised to the loudest slice. Real, not seeded — the take is in
 * memory as 16 kHz PCM the moment the recording stops. Silence is all
 * zeros rather than a division by nothing.
 */
export function takeEnvelope(samples, bars = COMPARE_BARS) {
  const length = samples?.length ?? 0
  if (length === 0) return Array.from({ length: bars }, () => 0)
  const slice = length / bars
  const levels = Array.from({ length: bars }, (_, index) => {
    const start = Math.floor(index * slice)
    const end = Math.max(start + 1, Math.floor((index + 1) * slice))
    let sum = 0
    for (let i = start; i < end && i < length; i++) sum += samples[i] * samples[i]
    return Math.sqrt(sum / Math.max(1, Math.min(end, length) - start))
  })
  const peak = Math.max(...levels)
  return peak > 0 ? levels.map((level) => level / peak) : levels
}

/** Bar heights for a strip, from 0..1 levels. */
export function envelopeHeights(levels) {
  return levels.map((level) => Math.round(COMPARE_BAR_MIN_PX + Math.min(1, Math.max(0, level)) * (COMPARE_BAR_MAX_PX - COMPARE_BAR_MIN_PX)))
}

/** The three steps of the design's sending state, in order — translated fresh on every call. */
export function sendSteps() {
  return [t('sendSteps.upload'), t('sendSteps.assess'), t('sendSteps.prepare')]
}

/**
 * Each step's state for a phase of the synchronous submit: the upload is
 * done when the browser has sent the last byte (a real event, not a
 * timer), the assessment is the wait for the answer, and the result is
 * prepared the moment the answer lands — so the third step is never
 * "active" for longer than a render.
 */
export function sendStepStates(phase) {
  switch (phase) {
    case 'assessing':
      return ['done', 'active', 'todo']
    case 'done':
      return ['done', 'done', 'done']
    default:
      return ['active', 'todo', 'todo']
  }
}

/**
 * The take's XP split (V6-w), from `AttemptDetailDto.aiXp` / `.socialXp`:
 * null — draw nothing — unless the take was scored under the V6 formula
 * (`aiXp` a number; the server sends null for every take scored under the
 * provisional rule, i.e. everything while `xp.formulaV2Enabled` is off).
 */
export function xpSplit(aiXp, socialXp) {
  if (typeof aiXp !== 'number') return null
  const social = typeof socialXp === 'number' ? socialXp : 0
  return { ai: aiXp, social, total: aiXp + social }
}
